// A verification form is an ordered list of sections, each an ordered list of typed fields.
// Mirrors backend/src/routes/verificationForms.ts.

export type FormSubject = "talent" | "organisation";

export type FieldType = "text" | "name" | "date" | "dropdown" | "id_document" | "file_upload" | "image_upload";

export interface FormField {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  helpText: string;
  placeholder?: string;
  /** dropdown only */
  options?: string[];
  /** id_document only */
  idSides?: "front" | "back" | "both";
  /** file_upload / image_upload only */
  fileMode?: "single" | "multiple";
}

export interface FormSection {
  id: string;
  title: string;
  description: string;
  fields: FormField[];
}

export interface VerificationForm {
  subject: FormSubject;
  sections: FormSection[];
  version: number;
  updated_by: string | null;
  updated_at: string;
}

export const FIELD_TYPES: { value: FieldType; label: string; bg: string; fg: string }[] = [
  { value: "text", label: "Text Field", bg: "#f3f4f6", fg: "#374151" },
  { value: "name", label: "Name", bg: "#f3f4f6", fg: "#374151" },
  { value: "date", label: "Date", bg: "#dbeafe", fg: "#1e40af" },
  { value: "dropdown", label: "Dropdown", bg: "#fef3c7", fg: "#92400e" },
  { value: "id_document", label: "ID Document", bg: "#fee2e2", fg: "#991b1b" },
  { value: "file_upload", label: "File Upload", bg: "#ede9fe", fg: "#5b21b6" },
  { value: "image_upload", label: "Image Upload", bg: "#fce7f3", fg: "#9d174d" },
];

export function fieldTypeMeta(t: FieldType) {
  return FIELD_TYPES.find((x) => x.value === t) ?? FIELD_TYPES[0];
}

/** A short note shown next to the type badge for types that carry an extra setting. */
export function fieldNote(f: FormField): string | null {
  if (f.type === "id_document") return f.idSides === "front" ? "Front only" : f.idSides === "back" ? "Back only" : "Front + Back";
  if (f.type === "file_upload" || f.type === "image_upload") return f.fileMode === "multiple" ? "Multiple files" : null;
  return null;
}

export function newId(prefix: string): string {
  const rand = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${rand}`;
}

export function blankField(): FormField {
  return { id: newId("f"), label: "", type: "text", required: true, helpText: "", placeholder: "" };
}

/** Stats shown above the tabs. */
export function formStats(sections: FormSection[]) {
  const fields = sections.flatMap((s) => s.fields);
  const required = fields.filter((f) => f.required).length;
  return { sections: sections.length, total: fields.length, required, optional: fields.length - required };
}

/** Moves item `from` to `to` (no-op out of bounds). */
export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
